// BusinessProfileTab.jsx
import React, { useEffect, useState } from "react";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";

import { Formik } from "formik";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import { SWAL_SETTINGS, INDIAN_STATES } from "../../../../utils/Constants";
import Select from "react-select";
import { capitalizeFirstLetter, globalLoader, handleServerValidations } from "../../../../utils/commonfunction";
import * as usersService from "../../services/seller.services";
import * as Yup from "yup";
import { Loader } from "rsuite";
import { useTranslation } from "react-i18next";

const BusinessProfileTab = () => {
  const navigate = useNavigate();
  const params = useParams();
  const [showdefault, setShowDefault] = useState({});
  const [previewImage, setPreviewImage] = useState("");
  const [dataLoaded, setDataLoaded] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    usersService
      .Details({ _id: params.id })
      .then((response) => {
        setShowDefault(response?.data || {});
        setPreviewImage(response?.data?.store_image || "");
        setTimeout(() => setDataLoaded(true), 100);
      })
      .catch((error) => console.log("error=====>", error));
  }, [params.id]);

  //   console.log("shoDefaultData",showdefault);

  const validationSchema = Yup.object().shape({
    business_handle: Yup.string().nullable(),
    store_name: Yup.string()
      .required(t("Store name is required"))
      .test("no-spaces", t("Store name is required"), (value) => value && value.trim().length > 0)
      .max(100, t("max_characters_error", { count: 100 })),
    store_address: Yup.string()
      .required(t("Store address is required"))
      .test("no-spaces", t("Store address is required"), (value) => value && value.trim().length > 0)
      .max(250, t("max_characters_error", { count: 250 })),
    state: Yup.string().nullable(),
    gst_number: Yup.string()
      .trim()
      .nullable()
      .test("gst-length", t("GST number must be 15 characters"), (value) => !value || value.length === 15)
      .test("valid-gst", t("Invalid GST number format"), (value) => {
        if (!value) return true;
        return /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(value.toUpperCase());
      }),
  });

  const handleImageChange = (e, setFieldValue) => {
    const file = e.target.files[0];
    if (file) {
      setFieldValue("store_image", file);
      const reader = new FileReader();
      reader.onloadend = () => setPreviewImage(reader.result);
      reader.readAsDataURL(file);
    }
  };

  if (!dataLoaded) {
    return <Loader center size="lg" />;
  }

  return (
    <Formik
      enableReinitialize
      initialValues={{
        business_handle: showdefault?.business_handle || "",
        store_name: showdefault?.store_name || "",
        store_address: showdefault?.store_location?.store_address || "",
        state: showdefault?.state || showdefault?.store_location?.state || "",
        gst_number: showdefault?.gst_number || "",
        store_image: null,
      }}
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting }) => {
        globalLoader(true);
        let formData = new FormData();
        formData.append("_id", params.id);
        formData.append("user", params.id);
        const rawHandle = values.business_handle ? values.business_handle.trim() : "";
        const cleanedHandle = rawHandle
          .replace(/^https?:\/\/[^\/]+\/businessprofilename\//i, "")
          .replace(/^businessprofilename\//i, "")
          .replace(/^@+/, "");
        formData.append("business_handle", cleanedHandle);
        formData.append("store_name", values.store_name);
        formData.append("store_address", values.store_address);
        formData.append("state", values.state || "");
        if (values.latitude) formData.append("latitude", values.latitude);
        if (values.longitude) formData.append("longitude", values.longitude);

        formData.append("gst_number", values.gst_number || "");
        if (values.store_image) {
          formData.append("store_image", values.store_image);
        }
        usersService
          .EditBusinessDetails(formData)
          .then((response) => {
            setSubmitting(false);
            globalLoader(false);
            if (response.success) {
              Swal.fire({
                icon: "success",
                text: response.message,
                ...SWAL_SETTINGS,
              });
            } else {
              Swal.fire({
                icon: "error",
                text: handleServerValidations(response),
                ...SWAL_SETTINGS,
              });
            }
          })
          .catch((error) => {
            console.log("error ====> ", error);
            globalLoader(false);
          });
      }}
    >
      {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, setFieldTouched }) => (
        <form onSubmit={handleSubmit}>
          <div className="row row-sm">
            {/* Profile Image Section */}
            <div className="col-md-12 text-center form-group mb-4">
              <div className="d-flex align-items-center justify-content-between bg-light p-4 rounded">
                <div className="d-flex align-items-center">
                  <div className="mr-3">
                    {previewImage ? (
                      <img
                        src={previewImage}
                        alt="Store"
                        style={{
                          width: "80px",
                          height: "80px",
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "80px",
                          height: "80px",
                          borderRadius: "50%",
                          backgroundColor: "#ddd",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <i className="fa fa-store fa-2x text-muted"></i>
                      </div>
                    )}
                  </div>
                  <h5 className="mb-0">{capitalizeFirstLetter(values.store_name)}</h5>
                </div>
                <label className="btn btn-coral" style={{ cursor: "pointer" }}>
                  {t("Upload Photo")}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageChange(e, setFieldValue)}
                    style={{ display: "none" }}
                  />
                </label>
              </div>
            </div>

            <div className="col-md-12 text-center form-group">
              <label htmlFor="business_handle" className="text-left d-flex">
                {t("seller_details.business_handle", "Business Profile Name")}:
              </label>
              <div className="input-group">
                <span className="input-group-text bg-light text-muted select-text cursor-text" style={{ fontSize: "14px", userSelect: "text" }}>
                  {window.location.origin}/businessprofilename/
                </span>
                <input
                  name="business_handle"
                  id="business_handle"
                  type="text"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.business_handle}
                  className="form-control"
                  placeholder={t("placeholder.business_handle", "Enter business profile name")}
                />
              </div>
              <span className="text-danger d-flex text-left">
                {errors.business_handle && touched.business_handle && errors.business_handle}
              </span>
            </div>

            <div className="col-md-12 text-center form-group">
              <label htmlFor="store_name" className="text-left d-flex">
                {t("Store Name")}:<span className="requirestar">*</span>
              </label>
              <input
                name="store_name"
                id="store_name"
                type="text"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.store_name}
                className="form-control"
                placeholder="Enter store name"
              />
              <span className="text-danger d-flex text-left">
                {errors.store_name && touched.store_name && errors.store_name}
              </span>
            </div>

            <div className="col-md-12 text-center form-group">
              <label htmlFor="store_address" className="text-left d-flex">
                {t("Store Address")}:<span className="requirestar">*</span>
              </label>
              {process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? (
                <GooglePlacesAutocomplete
                  apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                  selectProps={{
                    value: values.store_address ? { label: values.store_address, value: values.store_address } : null,
                    onChange: (val) => {
                      setFieldValue("store_address", val.label);
                      if (window.google) {
                        const geocoder = new window.google.maps.Geocoder();
                        geocoder.geocode({ address: val.label }, (results, status) => {
                          if (status === "OK") {
                            const lat = results[0].geometry.location.lat();
                            const lng = results[0].geometry.location.lng();
                            setFieldValue("latitude", lat);
                            setFieldValue("longitude", lng);
                          }
                        });
                      }
                    },
                    placeholder: t("Enter store address"),
                    classNamePrefix: "react-select",
                    styles: {
                      control: (provided) => ({
                        ...provided,
                        backgroundColor: "#f9fafb",
                        borderColor: "#e5e7eb",
                        borderRadius: "8px",
                        padding: "2px",
                        boxShadow: "none",
                        "&:hover": {
                          borderColor: "#d1d5db",
                        },
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999,
                      }),
                    },
                  }}
                />
              ) : (
                <input
                  name="store_address"
                  id="store_address"
                  type="text"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.store_address}
                  className="form-control"
                  placeholder="Enter store address"
                />
              )}
              <span className="text-danger d-flex text-left">
                {errors.store_address && touched.store_address && errors.store_address}
              </span>
            </div>

            <div className="col-md-12 text-center form-group">
              <label htmlFor="state" className="text-left d-flex">
                {t("State")}:
              </label>
              <Select
                id="state"
                name="state"
                options={INDIAN_STATES.map((st) => ({ value: st, label: st }))}
                className="react-select-container text-left"
                classNamePrefix="react-select"
                placeholder={t("Select State")}
                value={values.state ? { label: values.state, value: values.state } : null}
                onChange={(selectedOption) => {
                  setFieldValue("state", selectedOption ? selectedOption.value : "");
                }}
                onBlur={() => setFieldTouched("state", true)}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    backgroundColor: "#f9fafb",
                    borderColor: "#e5e7eb",
                    borderRadius: "8px",
                    padding: "2px",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#d1d5db",
                    },
                  }),
                  menu: (provided) => ({
                    ...provided,
                    zIndex: 9999,
                  }),
                }}
              />
              <span className="text-danger d-flex text-left">
                {errors.state && touched.state && errors.state}
              </span>
            </div>


            <div className="col-md-12 text-center form-group">
              <label htmlFor="gst_number" className="text-left d-flex">
                {t("GST Number")}:
              </label>
              <input
                name="gst_number"
                id="gst_number"
                type="text"
                onChange={(e) => {
                  const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
                  setFieldValue("gst_number", val);
                }}
                onBlur={handleBlur}
                value={values.gst_number}
                className="form-control"
                placeholder="e.g. 29ABCDE1234F1Z5"
                maxLength={15}
                style={{ textTransform: "uppercase" }}
              />
              <span className="text-danger d-flex text-left">
                {errors.gst_number && touched.gst_number && errors.gst_number}
              </span>
            </div>

            <div className="col-md-12">
              <button className="btn btn-success mr-2" type="submit">
                <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                {t("Update")}
              </button>
              <button
                className="btn ripple btn-secondary"
                type="button"
                onClick={() => navigate(-1)}
              >
                <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                {t("btn_cancel")}
              </button>
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
};

export default BusinessProfileTab;